import { useState } from 'react';
import { parsePhoneNumberFromString, getCountryCallingCode, getCountries, type CountryCode } from 'libphonenumber-js';
import type { CompanyDetails } from "./types";
import { industries, employeeSizes } from "./data";


type Props = {
  values: CompanyDetails;
  onChange: (next: CompanyDetails) => void;
  disabled?: boolean;
};

const inputCls =
  "w-full rounded-xl border border-border bg-card px-5 py-3 text-base text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary-soft))]";

const labelCls = "text-[1.05rem] font-semibold text-foreground";

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

export const CompanyForm = ({ values, onChange, disabled = false }: Props) => {
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [emailTouched, setEmailTouched] = useState(false);
  const set = <K extends keyof CompanyDetails>(key: K, v: CompanyDetails[K]) =>
    onChange({ ...values, [key]: v });

  const validateEmail = (email: string) => {
    if (!email.trim()) return "Work email is required.";
    if (!isValidEmail(email)) return "Please enter a valid email address (e.g. you@company.com).";
    return "";
  };

  // Helper to validate phone number
  const validatePhone = (full: string) => {
    const code = full.match(/^\+(\d{1,3})/)?.[1] || "91";
    const number = full.replace(/^\+\d{1,3}\s*/, "");
    // Map code to country (libphonenumber-js expects country code, not dial code)
    // We'll use a simple mapping for common codes
    const codeToCountry: Record<string, CountryCode> = {
      '1': 'US', '44': 'GB', '61': 'AU', '91': 'IN', '81': 'JP', '49': 'DE', '33': 'FR', '971': 'AE', '86': 'CN', '7': 'RU'
    };
    const country = codeToCountry[code] || 'IN';
    const phoneNumber = parsePhoneNumberFromString(number, country as CountryCode);
    if (!number) return "";
    if (!phoneNumber || !phoneNumber.isValid()) return "Invalid phone number for selected country code.";
    return "";
  };

  const handlePhoneChange = (code: string, number: string) => {
    const full = code + (number ? ' ' + number : '');
    set("phone", full);
    setPhoneError(validatePhone(full));
  };

  const code = values.phone.match(/^\+\d{1,3}/) ? values.phone.match(/^\+\d{1,3}/)[0] : '+1';
  const number = values.phone.replace(/^\+\d{1,3}\s*/, '');

  // Build all country code options
  const countryOptions = getCountries().map((country) => {
    const dialCode = getCountryCallingCode(country);
    return {
      code: `+${dialCode}`,
      label: `${country} (+${dialCode})`,
      country,
      dialCode
    };
  });
  // Remove duplicates (some countries share dial codes)
  let uniqueOptions = Array.from(
    new Map(countryOptions.map((item) => [item.code + item.country, item])).values()
  );
  // Only keep US for +1, remove all other +1 countries
  uniqueOptions = uniqueOptions.filter(opt => {
    if (opt.code === '+1') {
      return opt.country === 'US';
    }
    return true;
  });
  // Move US +1 to the top, then sort the rest
  const usIndex = uniqueOptions.findIndex(opt => opt.country === 'US' && opt.code === '+1');
  let usOption = null;
  if (usIndex !== -1) {
    usOption = uniqueOptions.splice(usIndex, 1)[0];
  }
  uniqueOptions.sort((a, b) => a.dialCode.localeCompare(b.dialCode) || a.country.localeCompare(b.country));
  if (usOption) {
    uniqueOptions = [usOption, ...uniqueOptions];
  }

  return (
    <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
      <div className="space-y-2 sm:col-span-2">
        <label className={labelCls}>Company Name *</label>
        <input
          className={inputCls}
          value={values.companyName}
          disabled={disabled}
          onChange={(e) => set("companyName", e.target.value)}
          placeholder="Acme Inc."
        />
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Work Email *</label>
        <input
          type="email"
          className={`${inputCls}${emailTouched && emailError ? " border-red-400 focus:border-red-500 focus:ring-red-100" : ""}`}
          value={values.email}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            set("email", next);
            if (emailTouched || next.length > 0) {
              setEmailTouched(true);
              setEmailError(validateEmail(next));
            }
          }}
          onBlur={() => {
            setEmailTouched(true);
            setEmailError(validateEmail(values.email));
          }}
          placeholder="you@company.com"
          autoComplete="email"
        />
        {emailTouched && emailError && (
          <div className="text-xs text-red-600 mt-1">{emailError}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Job Title *</label>
        <input
          className={inputCls}
          value={values.jobTitle}
          disabled={disabled}
          onChange={(e) => set("jobTitle", e.target.value)}
          placeholder="CFO, Head of Operations…"
        />
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Industry *</label>
        {industries.includes(values.industry) || values.industry === "" ? (
          <select
            className={inputCls}
            value={values.industry}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.value === "Other") {
                set("industry", "other");
              } else {
                set("industry", e.target.value);
              }
            }}
          >
            <option value="">Select industry</option>
            {industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
            {!industries.includes("Other") && <option value="Other">Other</option>}
          </select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              className={inputCls}
              value={values.industry === "other" ? "" : values.industry}
              disabled={disabled}
              onChange={(e) => set("industry", e.target.value)}
              placeholder="Please specify your industry"
              autoFocus
              style={{ flex: 1 }}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label="Show industry dropdown"
              className="rounded-full border border-border bg-card px-2 py-2 text-base text-muted-foreground hover:bg-muted transition-colors"
              onClick={() => set("industry", "")}
              disabled={disabled}
              style={{ lineHeight: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Employee Size *</label>
        <select
          className={inputCls}
          value={values.employeeSize}
          disabled={disabled}
          onChange={(e) => set("employeeSize", e.target.value)}
        >
          <option value="">Select size</option>
          {employeeSizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Website</label>
        <input
          className={inputCls}
          value={values.website}
          disabled={disabled}
          onChange={(e) => set("website", e.target.value)}
          placeholder="company.com"
        />
      </div>
      <div className="space-y-2">
        <label className={labelCls}>Phone</label>
        <div className="flex gap-2">
          <select
            className={inputCls + ' max-w-[100px] text-xs pr-2 text-left'}
            value={code}
            disabled={disabled}
            onChange={e => handlePhoneChange(e.target.value, number)}
          >
            {uniqueOptions.map((opt) => (
              <option key={opt.code + opt.country} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            className={inputCls}
            value={number}
            disabled={disabled}
            onChange={e => handlePhoneChange(code, e.target.value)}
            placeholder="Phone number"
            type="tel"
            pattern="[0-9 ]*"
          />
        </div>
        {phoneError && (
          <div className="text-xs text-red-600 mt-1">{phoneError}</div>
        )}
      </div>
    </div>
  );
};
